import { api } from "./client";
import type { ApiResponse } from "./types";

// ============================================================================
// The public surface — everything readable WITHOUT an account.
//
// This is the landing page's data supply, and it is the only part of WeDeshi a
// stranger ever sees. That makes it worth understanding as a set rather than
// as eight endpoints: each one answers "is there anything here for someone like
// me?" before a person is willing to hand over an email.
//
// Note what it deliberately does NOT expose. The community previews return
// COUNTS and sampled content, never people — you can learn that 340 Gujaratis
// near you are on WeDeshi, and nothing about who any of them are. The whole
// argument for a public preview collapses if it doubles as a scrape target.
// ============================================================================

/** One row of GET /public/states/map: the active states and union territories. */
export interface StateMapEntry {
  code: string;
  name: string;
  nameNative: string | null;
  region: string;
  /** Set when the state has its own theme. */
  primaryColor: string | null;
  mapData: { centroidLat: number | null; centroidLng: number | null; zoomLevel: number | null } | null;
}

/** One place in India's official list (GET /public/reference/india-regions). */
export interface IndiaRegion {
  code: string;
  aliases: string[];
  lgdCode: number;
  name: string;
  kind: string;
  nameNative: string | null;
  nativeLanguage: string | null;
  nameNativeConfirmed: boolean;
}

export interface IndiaRegionsReference {
  version: string;
  owner: string;
  sources: { field: string; source: string; url: string; checkedOn: string }[];
  regions: IndiaRegion[];
  retired: { code: string; name: string; successor: string; since: string }[];
}

export interface StateExperience {
  code: string;
  name: string;
  greeting: string | null;
  tagline: string | null;
  colors: Record<string, string> | null;
  patterns: string[];
  nostalgiaHooks: string[];
}

export interface StateAssets {
  code: string;
  mapSvgUrl: string | null;
  patternUrls: string[];
  ambientAudioUrl: string | null;
}

export interface CommunityStats {
  stateCode: string;
  memberCount: number;
  cityCounts: { city: string; count: number }[];
}

/** Every active state and union territory, with its theme colour if it has one. */
export function getStatesMap() {
  return api.get<ApiResponse<StateMapEntry[]>>("/public/states/map");
}

/**
 * India's 36 states and union territories: the server's one sourced list,
 * which clients keep a copy of for offline use.
 */
export function getIndiaRegions() {
  return api.get<ApiResponse<IndiaRegionsReference>>("/public/reference/india-regions");
}

/**
 * Asks for the list again with the copy's ETag. An unchanged list answers 304
 * with no body, which is how a client keeps its copy without downloading it.
 */
export async function checkIndiaRegionsEtag(etag: string): Promise<number> {
  const base = import.meta.env.VITE_API_URL || "http://localhost:7001";
  const res = await fetch(new URL("/v1/public/reference/india-regions", base), {
    headers: { "If-None-Match": etag },
    cache: "no-store",
  });
  return res.status;
}

/** The theming payload — greeting, palette, patterns, copy — for one state. */
export function getStateExperience(code: string) {
  return api.get<ApiResponse<StateExperience>>(`/public/state/${code}/experience`);
}

/** Just the asset URLs, split out so a client can preload without the copy. */
export function getStateAssets(code: string) {
  return api.get<ApiResponse<StateAssets>>(`/public/state/${code}/assets`);
}

/** What a brand-new client should assume before it knows anything about you. */
export function getPublicDefaults() {
  return api.get<ApiResponse<Record<string, unknown>>>("/public/defaults");
}

/** Counts only — never people. See the note at the top of this file. */
export function getCommunityStats(stateCode: string) {
  return api.get<ApiResponse<CommunityStats>>(`/public/community/${stateCode}/stats`);
}

/** A sampled taste of the community. Content, not identities. */
export function getCommunityPreview(stateCode: string) {
  return api.get<ApiResponse<Record<string, unknown>>>(`/public/community/${stateCode}/preview`);
}

export function getPublicEventsPreview() {
  return api.get<ApiResponse<Record<string, unknown>>>("/public/events/preview");
}

export function getPublicNostalgiaPreview() {
  return api.get<ApiResponse<Record<string, unknown>>>("/public/nostalgia/preview");
}

/** One metro (GET /reference/metros): the code every metro filter takes. */
export interface MetroArea {
  code: string;
  name: string;
  cities: string[];
}

/**
 * GET /reference/metros: every metro the server knows, with its towns. Every
 * filter that takes a metro code answers 400 UNKNOWN_METRO for any other code.
 */
export function getMetros() {
  return api.get<ApiResponse<MetroArea[]>>("/reference/metros");
}
